// frontend/src/pages/DepositsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';
import { Plus, Search, Edit, Trash2, X, Loader2, Package, CheckCircle, XCircle } from 'lucide-react';

// --- Modal de Formulário ---
const DepositFormModal = ({ depositToEdit, onClose, onSave }) => {
    const isEditing = !!depositToEdit;
    const { apiFetch } = useApi();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [deposit, setDeposit] = useState({
        name: depositToEdit?.name || '',
        description: depositToEdit?.description || '',
        status: depositToEdit?.status || 'ativo'
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setDeposit(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!deposit.name.trim()) {
            setError("O nome do depósito é obrigatório.");
            return;
        }

        setLoading(true);
        setError('');

        try {
            if (isEditing) {
                await apiFetch(`/deposits/${depositToEdit.id}`, { method: 'PUT', body: deposit });
            } else {
                await apiFetch('/deposits', { method: 'POST', body: deposit });
            }
            onSave();
        } catch (err) {
            setError(err.message || "Erro ao salvar depósito.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <Package size={20} className="text-blue-600" />
                        {isEditing ? 'Editar Depósito' : 'Novo Depósito'}
                    </h2>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md border border-red-200">
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Nome do Depósito <span className="text-red-500">*</span></label>
                        <input 
                            type="text" 
                            name="name" 
                            value={deposit.name} 
                            onChange={handleChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                            placeholder="Ex: Almoxarifado Principal, Loja Centro..." 
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Descrição</label>
                        <textarea 
                            name="description" 
                            value={deposit.description} 
                            onChange={handleChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none" 
                            placeholder="Descreva a finalidade ou localização deste depósito..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Status</label>
                        <select 
                            name="status" 
                            value={deposit.status} 
                            onChange={handleChange} 
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        >
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                        </select>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t mt-6">
                        <button type="button" onClick={onClose} disabled={loading} className="px-4 py-2 font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
                            Cancelar
                        </button>
                        <button type="submit" disabled={loading} className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center gap-2 transition-colors">
                            {loading && <Loader2 size={16} className="animate-spin" />}
                            Salvar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Página Principal ---
const DepositsPage = ({ dataChangeKey, currentUser }) => {
    const { apiFetch, permissions } = useApi();
    const [deposits, setDeposits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDeposit, setEditingDeposit] = useState(null);

    // Permissões
    const canCreate = permissions?.deposits_create || currentUser?.is_super_user;
    const canEdit = permissions?.deposits_edit || currentUser?.is_super_user;
    const canDelete = permissions?.deposits_delete || currentUser?.is_super_user;

    const fetchDeposits = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await apiFetch('/deposits');
            setDeposits(data || []);
        } catch (err) {
            setError('Erro ao carregar depósitos. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchDeposits();
    }, [fetchDeposits, dataChangeKey]);

    const handleOpenCreateModal = () => {
        setEditingDeposit(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (deposit) => {
        setEditingDeposit(deposit);
        setIsModalOpen(true);
    };

    const handleSave = () => {
        setIsModalOpen(false);
        fetchDeposits();
    };

    const handleDelete = async (id, name) => {
        if (window.confirm(`Tem certeza que deseja excluir o depósito "${name}"? Essa ação não pode ser desfeita.`)) {
            try {
                await apiFetch(`/deposits/${id}`, { method: 'DELETE' });
                fetchDeposits();
            } catch (err) {
                alert(`Erro ao excluir depósito: ${err.message}`);
            }
        }
    };

    // Filtragem local
    const filteredDeposits = deposits.filter(d => 
        d.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (d.description && d.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div className="p-3 md:p-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <Package className="text-blue-600" /> Cadastro de Depósitos
                    </h1>
                    <p className="text-sm md:text-base text-gray-500 mt-1">Gerencie os locais de estoque físico da sua empresa.</p>
                </div>
                {canCreate && (
                    <button 
                        onClick={handleOpenCreateModal}
                        className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 shadow-sm transition-all flex items-center justify-center gap-2"
                    >
                        <Plus size={20} /> Novo Depósito
                    </button>
                )}
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
                <div className="relative max-w-md">
                    <input 
                        type="text" 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Pesquisar por nome ou descrição..." 
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    />
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <Loader2 className="animate-spin text-blue-600" size={40} />
                </div>
            ) : error ? (
                <div className="bg-red-50 text-red-700 p-4 rounded-lg border border-red-200 text-center font-medium">
                    {error}
                </div>
            ) : filteredDeposits.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <Package size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-bold text-gray-700 mb-1">Nenhum depósito encontrado</h3>
                    <p className="text-gray-500">
                        {searchQuery ? 'Tente buscar com outros termos.' : 'Você ainda não cadastrou nenhum depósito.'}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredDeposits.map(deposit => (
                        <div key={deposit.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow flex flex-col">
                            <div className="flex justify-between items-start mb-3">
                                <h3 className="text-lg font-bold text-gray-800 line-clamp-1 flex-1 pr-2" title={deposit.name}>{deposit.name}</h3>
                                {deposit.status === 'ativo' ? (
                                    <span className="px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold rounded uppercase border border-green-200 flex items-center gap-1 shrink-0">
                                        <CheckCircle size={12} /> Ativo
                                    </span>
                                ) : (
                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold rounded uppercase border border-gray-200 flex items-center gap-1 shrink-0">
                                        <XCircle size={12} /> Inativo
                                    </span>
                                )}
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-4 flex-1 line-clamp-3" title={deposit.description || 'Sem descrição'}>
                                {deposit.description || <span className="italic opacity-50">Nenhuma descrição informada.</span>}
                            </p>
                            
                            <div className="pt-3 border-t border-gray-100 flex justify-end gap-2 mt-auto">
                                {canEdit && (
                                    <button 
                                        onClick={() => handleOpenEditModal(deposit)}
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors font-medium text-sm flex items-center gap-1"
                                    >
                                        <Edit size={16} /> Editar
                                    </button>
                                )}
                                {canDelete && (
                                    <button 
                                        onClick={() => handleDelete(deposit.id, deposit.name)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors font-medium text-sm flex items-center gap-1"
                                    >
                                        <Trash2 size={16} /> Excluir
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isModalOpen && (
                <DepositFormModal 
                    depositToEdit={editingDeposit} 
                    onClose={() => setIsModalOpen(false)} 
                    onSave={handleSave} 
                />
            )}
        </div>
    );
};

export default DepositsPage;